import { useEffect, useRef } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import {
    Monogram,
    Glyph,
    glyphPlacement,
    glyphRatio,
} from "../components/Monogram";
import { Rise } from "../components/Reveal";
import { COPY } from "../content/copy";
import { useTexts } from "../lib/settings";
import { scrollToId } from "../lib/scroll";
import { track } from "../lib/track";

gsap.registerPlugin(ScrollTrigger);

const MOTION_QUERY = "(prefers-reduced-motion: no-preference)";
const SMALL_QUERY = "(max-width: 900px)";

export default function Hero() {
    const rootRef = useRef(null);
    const monoRef = useRef(null);
    const colsBoxRef = useRef(null);
    const spansRef = useRef([]);
    const colLettersRef = useRef([]);
    const bodiesRef = useRef([]);
    const headlineRef = useRef(null);
    const t = useTexts();

    useEffect(() => {
        const mm = gsap.matchMedia();

        mm.add({ motion: MOTION_QUERY, small: SMALL_QUERY }, (ctx) => {
            if (!ctx.conditions.motion) return;
            const root = rootRef.current;
            const mono = monoRef.current;
            const spans = spansRef.current;
            const colLetters = colLettersRef.current;
            const bodies = bodiesRef.current;
            const bodyOf = (i) => [bodies[i * 2], bodies[i * 2 + 1]].filter(Boolean);
            const headline = headlineRef.current;
            const stage = root.querySelector(".hero-stage");

            document.documentElement.classList.add("pin-hero");
            document.body.classList.add("pin-mode");

            const letterVars = (span, i) => {
                const colLetter = colLetters[i];
                const parent = span.parentElement;
                return {
                    x: () => {
                        const box = colsBoxRef.current;
                        const targetCx =
                            box.offsetLeft - box.offsetWidth / 2 +
                            colLetter.offsetLeft + colLetter.offsetWidth / 2;
                        const baseCx =
                            parent.offsetLeft + span.offsetLeft + span.offsetWidth / 2;
                        return targetCx - baseCx;
                    },
                    y: () => {
                        const box = colsBoxRef.current;
                        const targetCy =
                            box.offsetTop - box.offsetHeight / 2 +
                            colLetter.offsetTop + colLetter.offsetHeight / 2;
                        const baseCy =
                            parent.offsetTop + span.offsetTop + span.offsetHeight / 2;
                        return targetCy - baseCy;
                    },
                    scale: () => colLetter.offsetWidth / span.offsetWidth,
                };
            };

            const tl = gsap.timeline({
                defaults: { ease: "power2.inOut" },
                scrollTrigger: {
                    trigger: stage,
                    start: "top top",
                    end: "+=250%",
                    scrub: 0.6,
                    pin: true,
                    anticipatePin: 1,
                    invalidateOnRefresh: true,
                    onUpdate: (self) =>
                        document.body.classList.toggle(
                            "header-visible",
                            self.progress >= 0.25
                        ),
                },
            });

            // 0.00 → 0.02 : le lettere si staccano dal monogramma (clip → libere)
            tl.to(
                mono.querySelector(".monogram-ink"),
                { opacity: 0, duration: 0.2, ease: "none" },
                0
            );
            tl.fromTo(
                ".hero-letters",
                { opacity: 0 },
                { opacity: 1, duration: 0.2, ease: "none" },
                0
            );

            // 0.00 → 0.25 : il triangolo atterra sul logo dell'header
            const logoTri = document.querySelector(".header-tri");
            tl.to(
                mono,
                {
                    x: () => {
                        const h = logoTri.getBoundingClientRect();
                        return h.left + h.width / 2 - window.innerWidth / 2;
                    },
                    y: () => {
                        const h = logoTri.getBoundingClientRect();
                        return h.top + h.height / 2 - window.innerHeight / 2;
                    },
                    scale: () => {
                        const h = logoTri.getBoundingClientRect();
                        return h.width / mono.offsetWidth;
                    },
                    duration: 2.5,
                },
                0
            );
            tl.to(
                ".hero-hint",
                { opacity: 0, duration: 0.3, ease: "none" },
                0.15
            );
            tl.to(mono, { opacity: 0, duration: 0.5, ease: "none" }, 2.55);

            // le lettere si staccano e si distribuiscono in tre colonne
            spans.forEach((span, i) => {
                if (!span) return;
                tl.to(
                    span,
                    { ...letterVars(span, i), duration: 2.5 },
                    i * 0.08
                );
            });

            // le colonne emergono mentre il triangolo atterra
            tl.fromTo(
                ".hero-cols",
                { opacity: 0 },
                { opacity: 1, duration: 1, ease: "none" },
                2.0
            );

            // 0.25 / 0.45 / 0.65 : i verbi appaiono sotto le rispettive lettere
            colLetters.forEach((_, i) => {
                tl.fromTo(
                    bodyOf(i),
                    { opacity: 0, y: 26 },
                    { opacity: 1, y: 0, duration: 2, ease: "power2.out" },
                    2.5 + i * 2
                );
            });

            // 0.83 → 0.91 : colonne e lettere escono; 0.91 → 1.00 : il titolo sale
            tl.to(
                spans.filter(Boolean),
                { opacity: 0, duration: 0.8, ease: "none" },
                8.3
            );
            tl.to(
                ".hero-cols",
                { opacity: 0, y: -40, duration: 0.8, ease: "power2.out" },
                8.3
            );
            tl.fromTo(
                headline,
                { yPercent: 115 },
                { yPercent: 0, duration: 1.1, ease: "power3.out" },
                8.9
            );

            // momento d'ingresso al caricamento
            gsap.from(stage, { opacity: 0, duration: 1.4, ease: "power2.out" });

            return () => {
                document.documentElement.classList.remove("pin-hero");
                document.body.classList.remove("pin-mode");
                document.body.classList.remove("header-visible");
            };
        });

        return () => mm.revert();
    }, []);

    return (
        <section className="hero" ref={rootRef} data-testid="hero-block-zero">
            <div className="hero-stage">
                <div className="hero-center-box hero-mono" ref={monoRef}>
                    <Monogram />
                </div>
                <div className="hero-cols" ref={colsBoxRef}>
                    {COPY.hero.cols.map((c, i) => (
                        <div className="hero-col" key={c.letter}>
                            <div className="hero-col-body">
                                <h3 className="hero-col-verb">
                                    <span
                                        className="hero-col-letter"
                                        aria-hidden="true"
                                        style={{ aspectRatio: glyphRatio(c.letter) }}
                                        ref={(el) => (colLettersRef.current[i] = el)}
                                    >
                                        <Glyph letter={c.letter} />
                                    </span>
                                    <span
                                        className="hero-col-rest"
                                        aria-hidden="true"
                                        ref={(el) => (bodiesRef.current[i * 2] = el)}
                                    >
                                        {c.verb.slice(1)}
                                    </span>
                                    <span className="sr-only">{c.verb}</span>
                                </h3>
                                <p
                                    className="hero-col-text"
                                    ref={(el) => (bodiesRef.current[i * 2 + 1] = el)}
                                >
                                    {c.text}
                                </p>
                            </div>
                        </div>
                    ))}
                </div>
                <div className="hero-center-box hero-letters">
                    {["A", "P", "F"].map((l, i) => (
                        <div
                            key={l}
                            className="hero-letter"
                            style={glyphPlacement(l)}
                            ref={(el) => (spansRef.current[i] = el)}
                        >
                            <Glyph letter={l} />
                        </div>
                    ))}
                </div>
                <div className="hero-headline-wrap">
                    <h1
                        className="display hero-headline"
                        ref={headlineRef}
                        data-testid="hero-headline"
                    >
                        {t.headline_a}{" "}
                        <em className="ital">{t.headline_b}</em>
                    </h1>
                </div>
                <div className="hero-hint caption">scorri</div>
                <button
                    type="button"
                    className="hero-skip caption"
                    data-testid="hero-skip"
                    onClick={() => {
                        track("block_zero_skip");
                        scrollToId("apertura");
                    }}
                >
                    salta
                </button>
            </div>

            <div className="hero-flow">
                <Monogram className="hero-flow-monogram" />
                <div className="hero-flow-cols">
                    {COPY.hero.cols.map((c, i) => (
                        <Rise
                            key={c.letter}
                            delay={i * 0.06}
                            className="hero-flow-col"
                        >
                            <h3 className="hero-col-verb">
                                <span
                                    className="flow-letter"
                                    aria-hidden="true"
                                    style={{ aspectRatio: glyphRatio(c.letter) }}
                                >
                                    <Glyph letter={c.letter} />
                                </span>
                                <span className="hero-col-rest" aria-hidden="true">{c.verb.slice(1)}</span>
                                <span className="sr-only">{c.verb}</span>
                            </h3>
                            <p className="hero-col-text">{c.text}</p>
                        </Rise>
                    ))}
                </div>
            </div>
        </section>
    );
}
